import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, Mail, AlertCircle } from 'lucide-react';

const WaitingForParent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const parentEmail = location.state?.parentEmail || 'your parent';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-36 mx-auto mb-4" />
        </div>

        {/* Waiting Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Clock Icon */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
              <Clock size={40} className="text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Thanks for Registering!
            </h1>
            <p className="text-gray-600">
              Your account is almost ready
            </p>
          </div>

          {/* Main Message */}
          <div className="space-y-6">
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-4">
                <Mail size={24} className="text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">
                    Parent Setup Required
                  </h3>
                  <p className="text-sm text-blue-800 mb-3">
                    We&apos;ve sent an email to <strong>{parentEmail}</strong> with instructions to complete the setup process.
                  </p>
                  <p className="text-sm text-blue-800">
                    Your account will be activated once your parent completes the setup and configures parental controls.
                  </p>
                </div>
              </div>
            </div>

            {/* What Happens Next */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">What happens next?</h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    1
                  </span>
                  <p className="text-sm text-gray-700 pt-0.5">
                    Your parent will receive an email with a secure setup link
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    2
                  </span>
                  <p className="text-sm text-gray-700 pt-0.5">
                    They&apos;ll verify their identity with a code sent via SMS
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    3
                  </span>
                  <p className="text-sm text-gray-700 pt-0.5">
                    They&apos;ll configure parental controls (content filters, time limits, privacy settings)
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    4
                  </span>
                  <p className="text-sm text-gray-700 pt-0.5">
                    You&apos;ll receive an email confirmation that your account is ready!
                  </p>
                </li>
              </ol>
            </div>

            {/* Console Tip */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-purple-900 mb-1">Demo Mode</p>
                  <p className="text-xs text-purple-700">
                    Check your browser console (F12 → Console) to see the parent setup email that was sent. The setup link is logged there.
                  </p>
                </div>
              </div>
            </div>

            {/* Why We Do This */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-sm font-semibold text-green-900 mb-2">
                🔒 Why do we require parent setup?
              </h4>
              <p className="text-xs text-green-800">
                HiBFF is committed to teen safety and COPPA compliance. Parent controls ensure you have a safe, healthy experience while giving your parents peace of mind.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/login')}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to Login
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              Check Status
            </button>
          </div>

          {/* Help Section */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Need help? Contact us at{' '}
              <a href="mailto:support@hibff.com" className="text-blue-600 hover:underline">
                support@hibff.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitingForParent;
